import { API } from "@swivl-mono/api-helper";
import { DateHelper } from "@swivl-mono/helpers";
import {
  ListTicketsRequestDTO,
  MyRoutingGroup,
  TaskActionType,
  TaskState,
  Ticket,
  TicketActivity,
  TicketDetail,
  TicketNote,
  TicketPreview,
  TicketPriority,
  TicketStatus,
  TicketTask,
  TicketTemplate
} from "@swivl-mono/types";
import { create } from "zustand";
import { devtools } from "zustand/middleware";

import { Member } from "../Member/Member.Current";
import NotificationsModel from "../Notifications/Notifications.Model";
import { getLastSeen, setLastSeen } from "./helpers/unread";

// Set by the ticket-access page — routes actions to the anonymous API.
let accessOverride: { token: string } | null = null;
export function setTicketAccessOverride(v: { token: string } | null): void {
  accessOverride = v;
  useTicketingStore.setState({ restrictedMode: v !== null });
}
export function getTicketAccessToken(): string | null {
  return accessOverride?.token ?? null;
}
// Shared branch for the 4 ticket-field updates access mode supports.
async function ticketUpdate<B>(
  ticketId: number,
  accessCall: (token: string, body: B) => Promise<{ ticket: Ticket }>,
  memberCall: (orgId: number, ticketId: number, body: B) => Promise<{ ticket: Ticket }>,
  body: B
): Promise<{ ticket: Ticket }> {
  const res = accessOverride ? await accessCall(accessOverride.token, body) : await memberCall(Member.orgId, ticketId, body);
  // No live socket for an anonymous actor — refetch so activity shows now.
  if (accessOverride) await useTicketingStore.getState().refreshSelected({ silent: true });
  return res;
}

type SortDir = "asc" | "desc";
type SortBy = "createdAt" | "updatedAt" | "status" | "priority";

/** Routing outcome of a freshly-created ticket — lets the inbox jump the nav to where it landed. */
export interface CreatedTicketResult {
  id: number;
  number: number;
  routingGroupSlug: string | null;
  assigneeMemberIds: number[];
  claimedByMemberId: number | null;
}

/** A "New ticket assigned to {group} group" arrival alert (rendered as a toast). */
export interface GroupTicketAlert {
  id: string;
  ticketId: number;
  ticketNumber: number;
  groupName: string;
  /** Optimistic-concurrency version, so the toast's Accept can claim directly. */
  version: number;
  createdAt: Date;
}

// Ticket ids we've already shown a group-arrival toast for, so repeated socket
// updates (or the create + socket double-path) for the same ticket don't
// re-alert. Session-scoped.
const alertedGroupTicketIds = new Set<number>();

type TicketingState = {
  tagOptions: string[];
  tagOptionsLoading: boolean;
  tagOptionsError: string | null;
  ensureTagsLoaded(): Promise<void>;
  addTagOption(tag: string): void;

  templates: TicketTemplate[];
  templatesLoading: boolean;
  templatesLoaded: boolean;
  templatesError: string | null;
  defaultTemplateId: number | null;
  loadTemplates(): Promise<void>;

  inboxQuery: ListTicketsRequestDTO & { sortBy?: SortBy; sortDir?: SortDir };
  inboxRows: TicketPreview[];
  inboxTotal: number;
  inboxLoading: boolean;
  inboxError: string | null;
  /**
   * How many rows the "available to accept" lane has returned so far.
   *
   * My Inbox paginates that lane alone (assigned tickets load in full up front),
   * so this — not `inboxRows.length` — is the offset its next page starts at.
   * The two differ by the number of assigned rows in hand, and using the
   * combined count skipped exactly that many acceptable tickets (ENG-2942).
   * Derived in refreshInbox, so it resets with every non-load-more query.
   */
  inboxAcceptanceOffset: number;

  // "Search All" page — an isolated query/result slice so it never clobbers the
  // inbox's own list filters (inboxQuery/inboxRows).
  searchQuery: ListTicketsRequestDTO & { sortBy?: SortBy; sortDir?: SortDir };
  searchRows: TicketPreview[];
  searchTotal: number;
  searchLoading: boolean;
  searchError: string | null;

  // Group Inbox nav rail — the groups the signed-in member belongs to, each
  // with its unaccepted-ticket badge count.
  myGroups: MyRoutingGroup[];
  myGroupsLoading: boolean;
  // True once loadMyGroups has resolved (success or failure) at least once.
  // Unlike myGroupsLoading, this never resets to false on later refreshes —
  // it's the signal consumers use to distinguish "still loading" from
  // "loaded and genuinely empty" (see ENG-2963).
  myGroupsLoaded: boolean;
  loadMyGroups(): Promise<void>;

  // "New ticket assigned to {group} group" arrival toasts (rendered by ToastRoot).
  groupTicketAlerts: GroupTicketAlert[];
  dismissGroupTicketAlert(id: string): void;
  /** Fire a group-arrival toast when `ticket` is a new open-to-group ticket in one of my groups. */
  maybeAlertNewGroupTicket(ticket: Pick<TicketPreview, "id" | "number" | "escalationState" | "routingGroupSlug" | "version" | "createdAt">): void;

  /**
   * One-shot signal to switch the inbox nav to "My Inbox". Bumped when a ticket
   * is accepted from the global arrival toast, which can't reach the inbox
   * view's local `viewMode` state directly. TicketInbox watches this counter and
   * selects My Inbox when it changes.
   */
  selectMyInboxNonce: number;
  requestMyInboxView(): void;

  /**
   * One-shot request to switch the inbox nav to All Tickets or a specific group
   * from outside TicketInbox (e.g. the Search All page's nav). Mirrors the
   * My-Inbox one-shot; TicketInbox applies it via a useEffect keyed on `nonce`.
   */
  inboxViewRequest: { mode: "all" | "group"; slug?: string; nonce: number } | null;
  requestAllView(): void;
  /** Pass a slug for a single group lane, or omit for the aggregate Group Inbox. */
  requestGroupView(slug?: string): void;
  /** Clear a consumed view request so it doesn't re-fire when TicketInbox remounts. */
  clearInboxViewRequest(): void;

  selectedTicketId: number | null;
  detailLoading: boolean;
  detailError: string | null;
  /** True inside the token-gated anonymous ticket view. */
  restrictedMode: boolean;

  ticket: Ticket | null;
  resolved: TicketDetail["resolved"] | null;
  tasks: TicketTask[];
  notes: TicketNote[];
  activity: TicketActivity[];

  lastActivityByTicketId: Record<number, { at: string; type: string }>;
  pendingByKey: Record<string, boolean>;

  setInboxQuery(patch: Partial<TicketingState["inboxQuery"]>): void;
  refreshInbox(): Promise<void>;

  setSearchQuery(patch: Partial<TicketingState["searchQuery"]>): void;
  refreshSearch(): Promise<void>;
  clearSearch(): void;

  createTicket(data: {
    title: string;
    description?: string | null;
    status?: TicketStatus;
    priority?: TicketPriority;
    tags?: string[];
    dueDate?: string | null;
    assigneeMemberIds?: number[];
    userId?: number | null;
    locationId?: number | null;
    templateId?: number;
    /** Cortex topic id — when set, server resolves the routing group and auto-assigns. */
    routingTopic?: string | null;
    /**
     * Target routing group slug. When set (and no explicit assignees), the new
     * ticket is dispatched into this group's inbox (open-to-group) right after
     * creation, so anyone in the group can accept it.
     */
    routingGroupSlug?: string | null;
  }): Promise<CreatedTicketResult>;

  selectTicket(ticketId: number | null): Promise<void>;
  refreshSelected(opts?: { silent?: boolean }): Promise<void>;
  /** Entry point for the anonymous /ticket-access/:token page. */
  loadByAccessToken(token: string): Promise<void>;
  markSelectedAsRead(): void;

  updateStatus(status: TicketStatus): Promise<void>;
  updatePriority(priority: TicketPriority): Promise<void>;
  updateTags(tags: string[]): Promise<void>;
  updateAssignees(assigneeMemberIds: number[]): Promise<void>;
  updateSubscribers(subscribedMemberIds: number[]): Promise<void>;
  updateTitle(title: string): Promise<void>;
  updateDescription(description: string | null): Promise<void>;
  updateDueDate(dueDate: string | null): Promise<void>;
  updateLocation(locationId: number | null): Promise<void>;
  updateUser(userId: number | null, userName?: string | null): Promise<void>;

  /** Claim the currently-selected ticket from the open-to-group lane. */
  acceptTicket(): Promise<void>;
  /** Claim any ticket (e.g. inline from an inbox list card) by id + version. Resolves true on a successful claim. */
  acceptTicketById(ticketId: number, expectedVersion: number): Promise<boolean>;

  /** Re-dispatch the selected ticket to a different routing group (reopens open-to-group). */
  updateGroup(routingGroupSlug: string): Promise<void>;

  addNote(body: string): Promise<void>;
  editNote(noteId: number, body: string): Promise<void>;
  deleteNote(noteId: number): Promise<void>;

  addTask(shortDescription: string): Promise<void>;
  setTaskState(taskId: number, state: TaskState): Promise<void>;
  deleteTask(taskId: number): Promise<void>;
  updateTaskDescription(taskId: number, shortDescription: string): Promise<void>;
  updateTaskAction(taskId: number, actionType: TaskActionType | null, actionConfig: Record<string, any> | null): Promise<void>;
  reorderTasks(taskIds: number[]): Promise<void>;

  // Socket event handlers
  onTicketUpdated(ticket: TicketPreview): void;
};

function key(op: string, id: number) {
  return `${op}:${id}`;
}

function isVersionConflict(e: unknown): boolean {
  return e instanceof Error && e.message === "VERSION_CONFLICT";
}

function computeHasUnread(ticketId: number, lastActivityAt?: string | Date | null) {
  const lastSeen = getLastSeen(ticketId);
  if (!lastActivityAt) return false;
  const la = DateHelper.toIsoString(lastActivityAt) as string;
  if (!lastSeen) return true;
  return new Date(la).getTime() > new Date(lastSeen).getTime();
}

const FALLBACK_TAGS = ["maintenance", "leasing", "billing", "noise", "parking", "pest_control", "move_in", "move_out", "inspection"];

/** Server's hard page ceiling for listTickets: any larger `limit` is clamped to this. */
const MY_INBOX_ASSIGNED_PAGE_SIZE = 200;

/** Runaway guard on the page walk below. 25 pages = 5,000 assigned tickets. */
const MY_INBOX_ASSIGNED_MAX_PAGES = 25;

/**
 * Fetch EVERY ticket in My Inbox's "Assigned to me" lane.
 *
 * That section doesn't lazy-load: it renders once at the top and stays put
 * while "Available to accept" paginates beneath it (see refreshInbox /
 * ENG-2942), so it has to be complete on first paint. Since the server clamps
 * any single page to MY_INBOX_ASSIGNED_PAGE_SIZE, completeness needs a walk
 * rather than one big `limit`.
 *
 * One request covers every realistic inbox — a second only fires for a member
 * holding a full page or more of assigned work.
 *
 * `query` must already be the assigned-lane query (`myInbox` set, group slugs
 * stripped); `offset`/`limit` here are ignored and driven by the walk.
 */
async function fetchAllAssignedTickets(query: object): Promise<any[]> {
  const rows: any[] = [];
  for (let page = 0; page < MY_INBOX_ASSIGNED_MAX_PAGES; page++) {
    const res = await API.Ticketing.listTickets(Member.orgId, {
      ...query,
      offset: page * MY_INBOX_ASSIGNED_PAGE_SIZE,
      limit: MY_INBOX_ASSIGNED_PAGE_SIZE
    } as any);
    const batch = (res.rows as any[]) ?? [];
    rows.push(...batch);
    // A short page means we've reached the end. The `total` check stops us when
    // a page comes back exactly full and there's nothing beyond it; both guards
    // together also cover a total that shrinks mid-walk (someone else claiming
    // a ticket between pages).
    if (batch.length < MY_INBOX_ASSIGNED_PAGE_SIZE || rows.length >= res.total) break;
  }
  return rows;
}

export const useTicketingStore = create<TicketingState>()(
  devtools(
    (set, get) => ({
      tagOptions: [],
      tagOptionsLoading: false,
      tagOptionsError: null,

      ensureTagsLoaded: async () => {
        if (get().tagOptionsLoading) return;
        if (get().tagOptions.length) return;

        set({ tagOptionsLoading: true }, false, "ticketing/tags:loading");
        try {
          const res = await API.Ticketing.listTags(Member.orgId);
          const tags = res.tags?.length ? res.tags : FALLBACK_TAGS;
          set({ tagOptions: tags, tagOptionsLoading: false }, false, "ticketing/tags:loaded");
        } catch {
          // Fallback on error
          set({ tagOptions: FALLBACK_TAGS, tagOptionsLoading: false }, false, "ticketing/tags:fallback");
        }
      },

      addTagOption: (tag: string) => {
        const current = get().tagOptions;
        if (!current.includes(tag)) {
          set({ tagOptions: [...current, tag].sort() }, false, "ticketing/tags:added");
        }
      },

      templates: [],
      templatesLoading: false,
      templatesLoaded: false,
      templatesError: null,
      defaultTemplateId: null,

      loadTemplates: async () => {
        if (get().templatesLoading) return;

        set({ templatesLoading: true, templatesError: null }, false, "ticketing/templates:loading");
        try {
          const [templatesRes, defaultRes] = await Promise.all([
            API.Ticketing.listTemplates(Member.orgId),
            API.Ticketing.getDefaultTemplate(Member.orgId)
          ]);

          const enabledTemplates = templatesRes.templates.filter((t) => t.enabled);
          const defaultId = defaultRes.template?.id ?? null;

          set(
            {
              templates: enabledTemplates,
              defaultTemplateId: defaultId,
              templatesLoading: false,
              templatesLoaded: true
            },
            false,
            "ticketing/templates:loaded"
          );
        } catch (e: any) {
          set({ templatesLoading: false, templatesError: e?.message ?? "Failed to load templates" }, false, "ticketing/templates:error");
        }
      },

      inboxQuery: { limit: 50, offset: 0, sortBy: "createdAt", sortDir: "desc" },
      inboxRows: [],
      inboxTotal: 0,
      inboxLoading: false,
      inboxError: null,
      inboxAcceptanceOffset: 0,

      searchQuery: { limit: 50, offset: 0, sortBy: "createdAt", sortDir: "desc" },
      searchRows: [],
      searchTotal: 0,
      searchLoading: false,
      searchError: null,

      myGroups: [],
      myGroupsLoading: false,
      myGroupsLoaded: false,

      loadMyGroups: async () => {
        if (get().myGroupsLoading) return;
        set({ myGroupsLoading: true }, false, "ticketing/myGroups:loading");
        try {
          const groups = await API.RoutingGroup.listMyGroups(Member.orgId);
          set({ myGroups: groups, myGroupsLoading: false, myGroupsLoaded: true }, false, "ticketing/myGroups:loaded");
        } catch {
          // Non-fatal — the rail just won't show group badges. Still mark as
          // loaded so callers waiting on myGroupsLoaded (e.g. My Inbox's
          // group-scoped query) don't wait forever on a failed load.
          set({ myGroupsLoading: false, myGroupsLoaded: true }, false, "ticketing/myGroups:error");
        }
      },

      groupTicketAlerts: [],
      dismissGroupTicketAlert: (id) => {
        set((s) => ({ groupTicketAlerts: s.groupTicketAlerts.filter((a) => a.id !== id) }), false, "ticketing/groupTicketAlert:dismiss");
      },

      selectMyInboxNonce: 0,
      requestMyInboxView: () => {
        set((s) => ({ selectMyInboxNonce: s.selectMyInboxNonce + 1 }), false, "ticketing/requestMyInboxView");
      },

      inboxViewRequest: null,
      requestAllView: () => {
        set((s) => ({ inboxViewRequest: { mode: "all", nonce: (s.inboxViewRequest?.nonce ?? 0) + 1 } }), false, "ticketing/requestAllView");
      },
      requestGroupView: (slug) => {
        set((s) => ({ inboxViewRequest: { mode: "group", slug, nonce: (s.inboxViewRequest?.nonce ?? 0) + 1 } }), false, "ticketing/requestGroupView");
      },
      clearInboxViewRequest: () => {
        set({ inboxViewRequest: null }, false, "ticketing/clearInboxViewRequest");
      },
      maybeAlertNewGroupTicket: (ticket) => {
        // Only unclaimed, group-routed tickets qualify.
        if (ticket.escalationState !== "open-to-group" || !ticket.routingGroupSlug) return;
        // Fire once per ticket, even across the create + socket double-path.
        if (alertedGroupTicketIds.has(ticket.id)) return;
        // Only for groups I belong to (I can see + accept them).
        const group = get().myGroups.find((g) => g.slug === ticket.routingGroupSlug);
        if (!group) return;
        // Only for freshly-arrived tickets — skip socket updates to old open-to-group tickets.
        const createdMs = ticket.createdAt ? new Date(ticket.createdAt).getTime() : 0;
        if (createdMs && Date.now() - createdMs > 60_000) return;

        alertedGroupTicketIds.add(ticket.id);
        const alert: GroupTicketAlert = {
          id: `gt-${ticket.id}`,
          ticketId: ticket.id,
          ticketNumber: ticket.number,
          groupName: group.name,
          version: ticket.version ?? 0,
          createdAt: new Date()
        };
        set((s) => ({ groupTicketAlerts: [...s.groupTicketAlerts, alert] }), false, "ticketing/groupTicketAlert:add");
      },

      selectedTicketId: null,
      detailLoading: false,
      detailError: null,
      restrictedMode: false,

      ticket: null,
      resolved: null,
      tasks: [],
      notes: [],
      activity: [],

      lastActivityByTicketId: {},
      pendingByKey: {},

      setInboxQuery: (patch) => {
        set((s) => ({ inboxQuery: { ...s.inboxQuery, ...patch } }), false, "ticketing/setInboxQuery");
      },

      refreshInbox: async () => {
        const q = get().inboxQuery;
        // Filter out undefined values to prevent them from being serialized as "undefined" strings
        const clean = (query: object): any => Object.fromEntries(Object.entries(query).filter(([, v]) => v !== undefined));
        const isLoadMore = (q.offset ?? 0) > 0;
        // My Inbox renders two sections from one flat list: "Assigned to me"
        // (everything not open-to-group) and "Available to accept". A single
        // union query paginates across both lanes, so a member with far more
        // acceptable than assigned tickets could get a first page of purely
        // acceptable ones — sorted oldest-first — and the "Assigned to me"
        // section would not render at all until enough pages were lazy-loaded
        // (ENG-2942). Fetching the two lanes separately keeps the section on
        // screen from mount, whatever the sort.
        //
        // Only the (much larger) acceptance lane paginates. Assigned tickets
        // load in full up front and are skipped entirely on load-more.
        const isMyInbox = !!q.myInbox;

        set({ inboxLoading: true, inboxError: null }, false, "ticketing/refreshInbox:start");
        try {
          const [assignedRows, mainRes] = await Promise.all([
            // Assigned lane: `myInbox` WITHOUT group slugs. Leaving the slugs on
            // is what made the server OR the two lanes into one paginated list.
            isMyInbox && !isLoadMore ? fetchAllAssignedTickets(clean({ ...q, routingGroupSlugs: undefined })) : undefined,
            // Acceptance lane: group slugs WITHOUT `myInbox`. The server scopes
            // this lane to the slugs the caller sends (see listTickets).
            API.Ticketing.listTickets(Member.orgId, isMyInbox ? clean({ ...q, myInbox: undefined, openForAcceptance: true }) : clean(q))
          ]);

          const lastMap = { ...get().lastActivityByTicketId };
          const enrich = (rows: any[]): TicketPreview[] =>
            rows.map((r: any) => {
              const cached = lastMap[r.id];
              const lastActivityAt = r.lastActivityAt ?? cached?.at;
              const lastActivityType = r.lastActivityType ?? cached?.type;
              return {
                ...r,
                lastActivityAt,
                lastActivityType,
                hasUnreadActivity: computeHasUnread(r.id, lastActivityAt)
              };
            });

          // Assigned first so the section order matches the rendered list. The
          // two lanes are disjoint by escalationState, but dedupe anyway so a
          // ticket that changed lane mid-flight can't render twice.
          const assigned = enrich(assignedRows ?? []);
          const assignedIds = new Set(assigned.map((r) => r.id));
          const mainRows = (mainRes.rows as any[]) ?? [];
          const enriched = [...assigned, ...enrich(mainRows).filter((r) => !assignedIds.has(r.id))];

          // Advance the acceptance lane's own cursor. Counted from what the
          // server returned rather than what survived the dedupe above, since
          // it has to mirror how far into that lane the server has read: a
          // deduped row is still a row the next page must skip.
          const acceptanceOffset = (isLoadMore ? get().inboxAcceptanceOffset : 0) + mainRows.length;

          // Append rows when loading more (offset > 0), replace when new query (offset === 0)
          const newRows = isLoadMore ? [...get().inboxRows, ...enriched] : enriched;
          // `mainRes.total` counts only the acceptance lane in My Inbox, so add
          // the assigned rows we hold. Counting rows rather than a server total
          // keeps `hasMore` honest: it can never exceed what's actually loaded,
          // so the scroll sentinel always terminates.
          const total = isMyInbox ? newRows.filter((r) => r.escalationState !== "open-to-group").length + mainRes.total : mainRes.total;
          set(
            { inboxRows: newRows, inboxTotal: total, inboxAcceptanceOffset: acceptanceOffset, inboxLoading: false },
            false,
            "ticketing/refreshInbox:success"
          );
        } catch (e: any) {
          set({ inboxLoading: false, inboxError: e?.message ?? "Failed to load inbox" }, false, "ticketing/refreshInbox:error");
        }
      },

      setSearchQuery: (patch) => {
        set((s) => ({ searchQuery: { ...s.searchQuery, ...patch } }), false, "ticketing/setSearchQuery");
      },

      refreshSearch: async () => {
        const q = get().searchQuery;
        // Nothing to search on: no keyword and no filters → show the empty state
        // (skip the API call so an empty Search All page stays empty).
        const hasCriteria =
          !!q.q?.trim() ||
          !!q.assigneeMemberIds?.length ||
          !!q.routingGroupSlugs?.length ||
          !!q.statuses?.length ||
          !!q.locationIds?.length ||
          !!q.priority;
        if (!hasCriteria) {
          set({ searchRows: [], searchTotal: 0, searchLoading: false, searchError: null }, false, "ticketing/refreshSearch:empty");
          return;
        }

        const cleanQuery = Object.fromEntries(Object.entries(q).filter(([, v]) => v !== undefined));
        set({ searchLoading: true, searchError: null }, false, "ticketing/refreshSearch:start");
        try {
          const res = await API.Ticketing.listTickets(Member.orgId, cleanQuery as any);
          const rows = (res.rows as TicketPreview[]) ?? [];
          set({ searchRows: rows, searchTotal: res.total, searchLoading: false }, false, "ticketing/refreshSearch:success");
        } catch (e: any) {
          set({ searchLoading: false, searchError: e?.message ?? "Failed to search tickets" }, false, "ticketing/refreshSearch:error");
        }
      },

      clearSearch: () => {
        set(
          {
            searchQuery: { limit: 50, offset: 0, sortBy: "createdAt", sortDir: "desc" },
            searchRows: [],
            searchTotal: 0,
            searchError: null
          },
          false,
          "ticketing/clearSearch"
        );
      },

      createTicket: async (data) => {
        // `routingGroupSlug` isn't part of the create API — we dispatch to the
        // group as a follow-up step, so pull it out before creating.
        const { routingGroupSlug, ...createData } = data;
        const res = await API.Ticketing.createTicket(Member.orgId, createData as any);
        let ticket = res.ticket;

        // If a destination group was picked and there are no explicit assignees,
        // dispatch the freshly-created ticket into that group's inbox
        // (open-to-group) via the same endpoint the detail-panel Group field
        // uses. Explicit assignees win over group routing, mirroring topic logic.
        const hasExplicitAssignees = (createData.assigneeMemberIds?.length ?? 0) > 0;
        if (routingGroupSlug && !hasExplicitAssignees) {
          try {
            const dispatched = await API.Ticketing.updateTicketGroup(Member.orgId, ticket.id, {
              expectedVersion: ticket.version,
              routingGroupSlug
            } as any);
            ticket = dispatched.ticket;
          } catch (e) {
            const msg = e instanceof Error ? e.message : String(e);
            NotificationsModel.createToastNotification({ message: `Ticket created, but couldn't route it to the group: ${msg}` });
          }
        }

        // Refresh inbox to show the new ticket, and the group badge counts — a
        // ticket dispatched to a group bumps that group's unaccepted count.
        await get().refreshInbox();
        void get().loadMyGroups();
        // If I just dispatched it to one of my groups, show the arrival toast too
        // (covers the case where the socket event doesn't reach my own session).
        get().maybeAlertNewGroupTicket(ticket);
        return {
          id: ticket.id,
          number: ticket.number,
          routingGroupSlug: ticket.routingGroupSlug ?? null,
          assigneeMemberIds: ticket.assigneeMemberIds ?? [],
          claimedByMemberId: ticket.claimedByMemberId ?? null
        };
      },

      selectTicket: async (ticketId) => {
        set(
          {
            selectedTicketId: ticketId,
            detailError: null,
            detailLoading: !!ticketId,
            ticket: null,
            resolved: null,
            tasks: [],
            notes: [],
            activity: []
          },
          false,
          "ticketing/selectTicket"
        );
        if (!ticketId) return;
        await get().ensureTagsLoaded();
        await get().refreshSelected();
        get().markSelectedAsRead();
      },

      loadByAccessToken: async (token) => {
        setTicketAccessOverride({ token });
        // Clear any ticket left over from prior browsing in this tab.
        set(
          { detailLoading: true, detailError: null, ticket: null, resolved: null, notes: [], activity: [] },
          false,
          "ticketing/loadByAccessToken:start"
        );
        try {
          const res = await API.TicketingAccess.getTicket(token);
          set(
            {
              selectedTicketId: res.ticket.id,
              ticket: res.ticket as any,
              resolved: res.resolved ?? null,
              notes: res.notes ?? [],
              activity: res.activity ?? [],
              detailLoading: false
            },
            false,
            "ticketing/loadByAccessToken:success"
          );
        } catch (e: any) {
          set({ detailLoading: false, detailError: e?.message ?? "This link is no longer valid." }, false, "ticketing/loadByAccessToken:error");
        }
      },

      refreshSelected: async (opts) => {
        const ticketId = get().selectedTicketId;
        if (!ticketId) return;

        const silent = opts?.silent ?? false;

        if (!silent) {
          set({ detailLoading: true, detailError: null }, false, "ticketing/refreshSelected:start");
        }

        try {
          const res = accessOverride
            ? await API.TicketingAccess.getTicket(accessOverride.token)
            : await API.Ticketing.getTicketDetail(Member.orgId, ticketId);

          const newest = res.activity?.[0];
          if (newest) {
            set((s) => ({
              lastActivityByTicketId: {
                ...s.lastActivityByTicketId,
                [ticketId]: { at: DateHelper.toIsoString(newest.createdAt)!, type: newest.type }
              }
            }));
          }

          set(
            {
              ticket: res.ticket as any,
              resolved: res.resolved ?? null,
              tasks: res.tasks ?? [],
              notes: res.notes ?? [],
              activity: res.activity ?? [],
              ...(silent ? {} : { detailLoading: false })
            },
            false,
            "ticketing/refreshSelected:success"
          );
        } catch (e: any) {
          if (!silent) {
            set({ detailLoading: false, detailError: e?.message ?? "Failed to load ticket" }, false, "ticketing/refreshSelected:error");
          }
        }
      },

      markSelectedAsRead: () => {
        const t = get().ticket;
        const acts = get().activity;
        if (!t) return;
        const newest = acts?.[0];
        if (!newest) return;
        const iso = DateHelper.toIsoString(newest.createdAt);
        if (!iso) return;
        setLastSeen(t.id, iso);
        set((s) => ({
          inboxRows: s.inboxRows.map((r) => (r.id === t.id ? { ...r, hasUnreadActivity: false } : r))
        }));
      },

      updateStatus: async (status) => {
        const t = get().ticket;
        if (!t) return;
        const opKey = key("status", t.id);
        if (get().pendingByKey[opKey]) return;

        const prevTicket = { ...t };

        set(
          (s) => ({
            pendingByKey: { ...s.pendingByKey, [opKey]: true },
            ticket: s.ticket ? ({ ...s.ticket, status, version: s.ticket.version + 1 } as any) : s.ticket,
            inboxRows: s.inboxRows.map((r) => (r.id === t.id ? { ...r, status } : r))
          }),
          false,
          "ticketing/updateStatus:optimistic"
        );

        try {
          const res = await ticketUpdate(t.id, API.TicketingAccess.updateStatus, API.Ticketing.updateTicketStatus, {
            expectedVersion: prevTicket.version,
            status
          } as any);
          set({ ticket: res.ticket as any }, false, "ticketing/updateStatus:commit");
        } catch (e) {
          set(
            (s) => ({
              ticket: prevTicket as any,
              inboxRows: s.inboxRows.map((r) => (r.id === prevTicket.id ? { ...r, status: prevTicket.status } : r))
            }),
            false,
            "ticketing/updateStatus:rollback"
          );
          if (isVersionConflict(e)) {
            NotificationsModel.createToastNotification({ message: "Ticket was modified by someone else. Refreshing..." });
            void get().refreshSelected({ silent: true });
            return;
          }
          throw e;
        } finally {
          set(
            (s) => {
              const next = { ...s.pendingByKey };
              delete next[opKey];
              return { pendingByKey: next };
            },
            false,
            "ticketing/updateStatus:done"
          );
        }
      },

      acceptTicket: async () => {
        const t = get().ticket;
        if (!t) return;
        await get().acceptTicketById(t.id, t.version);
      },

      acceptTicketById: async (ticketId, expectedVersion) => {
        const opKey = key("accept", ticketId);
        if (get().pendingByKey[opKey]) return false;

        set((s) => ({ pendingByKey: { ...s.pendingByKey, [opKey]: true } }), false, "ticketing/acceptTicket:start");

        try {
          const res = await API.Ticketing.acceptTicket(Member.orgId, ticketId, { expectedVersion });
          const claimed = res.ticket as any;
          set(
            (s) => ({
              // Update detail if this is the open ticket; the claimer now owns it.
              ticket: s.ticket?.id === ticketId ? claimed : s.ticket,
              // Update the row in place rather than removing it — the ticket
              // stays in the Group Inbox (now under "Accepted") and moves from
              // "Available to accept" → "Assigned to me" in My Inbox. The split
              // in both views keys off escalationState.
              inboxRows: s.inboxRows.map((r) =>
                r.id === ticketId
                  ? {
                      ...r,
                      escalationState: claimed?.escalationState ?? r.escalationState,
                      assigneeMemberIds: claimed?.assigneeMemberIds ?? r.assigneeMemberIds
                    }
                  : r
              )
            }),
            false,
            "ticketing/acceptTicket:commit"
          );
          // No success toast — accepting routes the claimer straight to the ticket.
          // Re-fetch so the accepted ticket resettles correctly (authoritative
          // ordering: pending on top, accepted below) and the group badges update.
          void get().refreshInbox();
          void get().loadMyGroups();
          return true;
        } catch (e) {
          // Race: another agent claimed it first. The optimistic-concurrency
          // check fails as either a version conflict (our row was stale) or a
          // "not open for acceptance" error (already claimed). Either way the
          // loser gets a clear alert and a refresh so the ticket leaves their lane.
          const msg = e instanceof Error ? e.message : String(e);
          if (isVersionConflict(e) || msg.includes("TICKET_NOT_OPEN_FOR_ACCEPTANCE")) {
            NotificationsModel.createToastNotification({
              title: "Ticket already taken",
              message: "Someone else accepted this ticket first."
            });
            if (get().selectedTicketId === ticketId) void get().refreshSelected({ silent: true });
            void get().refreshInbox();
            void get().loadMyGroups();
            return false;
          }
          NotificationsModel.createToastNotification({ message: `Couldn't accept ticket: ${msg}` });
          return false;
        } finally {
          set(
            (s) => {
              const next = { ...s.pendingByKey };
              delete next[opKey];
              return { pendingByKey: next };
            },
            false,
            "ticketing/acceptTicket:done"
          );
        }
      },

      updateGroup: async (routingGroupSlug) => {
        const t = get().ticket;
        if (!t) return;
        const opKey = key("group", t.id);
        if (get().pendingByKey[opKey]) return;

        const prevTicket = { ...t };

        // Optimistically reflect the re-dispatch: reopened to the new group,
        // claim/assignees cleared.
        set(
          (s) => ({
            pendingByKey: { ...s.pendingByKey, [opKey]: true },
            ticket: s.ticket
              ? ({
                  ...s.ticket,
                  routingGroupSlug,
                  escalationState: "open-to-group",
                  claimedByMemberId: null,
                  assigneeMemberIds: [],
                  version: s.ticket.version + 1
                } as any)
              : s.ticket
          }),
          false,
          "ticketing/updateGroup:optimistic"
        );

        try {
          const res = await API.Ticketing.updateTicketGroup(Member.orgId, t.id, {
            expectedVersion: prevTicket.version,
            routingGroupSlug
          } as any);
          set({ ticket: res.ticket as any }, false, "ticketing/updateGroup:commit");
          void get().loadMyGroups();
          void get().refreshInbox();
        } catch (e) {
          set({ ticket: prevTicket as any }, false, "ticketing/updateGroup:rollback");
          if (isVersionConflict(e)) {
            NotificationsModel.createToastNotification({ message: "Ticket was modified by someone else. Refreshing..." });
            void get().refreshSelected({ silent: true });
            return;
          }
          const msg = e instanceof Error ? e.message : String(e);
          NotificationsModel.createToastNotification({ message: `Couldn't change group: ${msg}` });
        } finally {
          set(
            (s) => {
              const next = { ...s.pendingByKey };
              delete next[opKey];
              return { pendingByKey: next };
            },
            false,
            "ticketing/updateGroup:done"
          );
        }
      },

      updatePriority: async (priority) => {
        const t = get().ticket;
        if (!t) return;
        const opKey = key("priority", t.id);
        if (get().pendingByKey[opKey]) return;

        const prevTicket = { ...t };

        set(
          (s) => ({
            pendingByKey: { ...s.pendingByKey, [opKey]: true },
            ticket: s.ticket ? ({ ...s.ticket, priority, version: s.ticket.version + 1 } as any) : s.ticket,
            inboxRows: s.inboxRows.map((r) => (r.id === t.id ? { ...r, priority } : r))
          }),
          false,
          "ticketing/updatePriority:optimistic"
        );

        try {
          const res = await ticketUpdate(t.id, API.TicketingAccess.updatePriority, API.Ticketing.updateTicketPriority, {
            expectedVersion: prevTicket.version,
            priority
          } as any);
          set({ ticket: res.ticket as any }, false, "ticketing/updatePriority:commit");
        } catch (e) {
          set(
            (s) => ({
              ticket: prevTicket as any,
              inboxRows: s.inboxRows.map((r) => (r.id === prevTicket.id ? { ...r, priority: prevTicket.priority } : r))
            }),
            false,
            "ticketing/updatePriority:rollback"
          );
          if (isVersionConflict(e)) {
            NotificationsModel.createToastNotification({ message: "Ticket was modified by someone else. Refreshing..." });
            void get().refreshSelected({ silent: true });
            return;
          }
          throw e;
        } finally {
          set(
            (s) => {
              const next = { ...s.pendingByKey };
              delete next[opKey];
              return { pendingByKey: next };
            },
            false,
            "ticketing/updatePriority:done"
          );
        }
      },

      updateTags: async (tags) => {
        const t = get().ticket;
        if (!t) return;
        const opKey = key("tags", t.id);
        if (get().pendingByKey[opKey]) return;

        const prevTicket = { ...t };
        const prevTags = [...(t.tags ?? [])];

        set(
          (s) => ({
            pendingByKey: { ...s.pendingByKey, [opKey]: true },
            ticket: s.ticket ? ({ ...s.ticket, tags, version: s.ticket.version + 1 } as any) : s.ticket
          }),
          false,
          "ticketing/updateTags:optimistic"
        );

        try {
          const res = await ticketUpdate(t.id, API.TicketingAccess.updateTags, API.Ticketing.updateTicketTags, {
            expectedVersion: prevTicket.version,
            tags
          } as any);
          set({ ticket: res.ticket as any }, false, "ticketing/updateTags:commit");
        } catch (e) {
          set(
            (s) => ({
              ticket: s.ticket ? ({ ...s.ticket, tags: prevTags, version: prevTicket.version } as any) : s.ticket
            }),
            false,
            "ticketing/updateTags:rollback"
          );
          if (isVersionConflict(e)) {
            NotificationsModel.createToastNotification({ message: "Ticket was modified by someone else. Refreshing..." });
            void get().refreshSelected({ silent: true });
            return;
          }
          throw e;
        } finally {
          set(
            (s) => {
              const next = { ...s.pendingByKey };
              delete next[opKey];
              return { pendingByKey: next };
            },
            false,
            "ticketing/updateTags:done"
          );
        }
      },

      updateAssignees: async (assigneeMemberIds) => {
        const t = get().ticket;
        if (!t) return;
        const opKey = key("assignees", t.id);
        if (get().pendingByKey[opKey]) return;

        const prevTicket = { ...t };
        const prevAssignees = [...(t.assigneeMemberIds ?? [])];
        const prevEscalationState = t.escalationState;

        // Manually unassigning a claimed group ticket reverts it to the group's
        // "open to accept" lane (the backend does the authoritative revert). Flip
        // the acceptance state optimistically so the Accept button + Available lane
        // resurface immediately instead of waiting for the next refresh.
        const isGroupUnassignRevert = (assigneeMemberIds?.length ?? 0) === 0 && !!t.routingGroupSlug && t.escalationState === "claimed";

        // Manually assigning an individual to a still-open group ticket claims
        // it — mirrors the backend's forward transition so the ticket doesn't
        // flash in "Available to accept" while the request round-trips.
        const isIndividualAssignClaim =
          (assigneeMemberIds?.length ?? 0) > 0 && (t.escalationState === "open-to-group" || t.escalationState === "processing");
        const claimingMemberId = assigneeMemberIds?.length === 1 ? assigneeMemberIds[0] : null;

        set(
          (s) => ({
            pendingByKey: { ...s.pendingByKey, [opKey]: true },
            ticket: s.ticket
              ? ({
                  ...s.ticket,
                  assigneeMemberIds,
                  ...(isGroupUnassignRevert ? { escalationState: "open-to-group", claimedByMemberId: null } : {}),
                  ...(isIndividualAssignClaim ? { escalationState: "claimed", claimedByMemberId: claimingMemberId } : {}),
                  version: s.ticket.version + 1
                } as any)
              : s.ticket,
            inboxRows: s.inboxRows.map((r) =>
              r.id === t.id
                ? {
                    ...r,
                    assigneeMemberIds,
                    ...(isGroupUnassignRevert ? { escalationState: "open-to-group" as const } : {}),
                    ...(isIndividualAssignClaim ? { escalationState: "claimed" as const } : {})
                  }
                : r
            )
          }),
          false,
          "ticketing/updateAssignees:optimistic"
        );

        try {
          const res = await API.Ticketing.updateTicketAssignees(Member.orgId, t.id, {
            expectedVersion: prevTicket.version,
            assigneeMemberIds
          } as any);
          set({ ticket: res.ticket as any }, false, "ticketing/updateAssignees:commit");
          if (isGroupUnassignRevert || isIndividualAssignClaim) {
            void get().loadMyGroups();
            void get().refreshInbox();
          }
        } catch (e) {
          set(
            (s) => ({
              ticket: prevTicket as any,
              inboxRows: s.inboxRows.map((r) =>
                r.id === prevTicket.id
                  ? {
                      ...r,
                      assigneeMemberIds: prevAssignees,
                      ...(isGroupUnassignRevert || isIndividualAssignClaim ? { escalationState: prevEscalationState } : {})
                    }
                  : r
              )
            }),
            false,
            "ticketing/updateAssignees:rollback"
          );
          if (isVersionConflict(e)) {
            NotificationsModel.createToastNotification({ message: "Ticket was modified by someone else. Refreshing..." });
            void get().refreshSelected({ silent: true });
            return;
          }
          throw e;
        } finally {
          set(
            (s) => {
              const next = { ...s.pendingByKey };
              delete next[opKey];
              return { pendingByKey: next };
            },
            false,
            "ticketing/updateAssignees:done"
          );
        }
      },

      updateSubscribers: async (subscribedMemberIds) => {
        const t = get().ticket;
        if (!t) return;
        const opKey = key("subscribers", t.id);
        if (get().pendingByKey[opKey]) return;

        const prevTicket = { ...t };

        set(
          (s) => ({
            pendingByKey: { ...s.pendingByKey, [opKey]: true },
            ticket: s.ticket ? ({ ...s.ticket, subscribedMemberIds, version: s.ticket.version + 1 } as any) : s.ticket
          }),
          false,
          "ticketing/updateSubscribers:optimistic"
        );

        try {
          const res = await API.Ticketing.updateTicketSubscribers(Member.orgId, t.id, {
            expectedVersion: prevTicket.version,
            subscribedMemberIds
          } as any);
          set({ ticket: res.ticket as any }, false, "ticketing/updateSubscribers:commit");
        } catch (e) {
          set({ ticket: prevTicket as any }, false, "ticketing/updateSubscribers:rollback");
          if (isVersionConflict(e)) {
            NotificationsModel.createToastNotification({ message: "Ticket was modified by someone else. Refreshing..." });
            void get().refreshSelected({ silent: true });
            return;
          }
          throw e;
        } finally {
          set(
            (s) => {
              const next = { ...s.pendingByKey };
              delete next[opKey];
              return { pendingByKey: next };
            },
            false,
            "ticketing/updateSubscribers:done"
          );
        }
      },

      updateTitle: async (title) => {
        const t = get().ticket;
        if (!t) return;
        const opKey = key("title", t.id);
        if (get().pendingByKey[opKey]) return;

        const prevTicket = { ...t };

        set(
          (s) => ({
            pendingByKey: { ...s.pendingByKey, [opKey]: true },
            ticket: s.ticket ? ({ ...s.ticket, title, version: s.ticket.version + 1 } as any) : s.ticket,
            inboxRows: s.inboxRows.map((r) => (r.id === t.id ? { ...r, title } : r))
          }),
          false,
          "ticketing/updateTitle:optimistic"
        );

        try {
          const res = await API.Ticketing.updateTicketTitle(Member.orgId, t.id, { expectedVersion: prevTicket.version, title } as any);
          set({ ticket: res.ticket as any }, false, "ticketing/updateTitle:commit");
        } catch (e) {
          set(
            (s) => ({
              ticket: prevTicket as any,
              inboxRows: s.inboxRows.map((r) => (r.id === prevTicket.id ? { ...r, title: prevTicket.title } : r))
            }),
            false,
            "ticketing/updateTitle:rollback"
          );
          if (isVersionConflict(e)) {
            NotificationsModel.createToastNotification({ message: "Ticket was modified by someone else. Refreshing..." });
            void get().refreshSelected({ silent: true });
            return;
          }
          throw e;
        } finally {
          set(
            (s) => {
              const next = { ...s.pendingByKey };
              delete next[opKey];
              return { pendingByKey: next };
            },
            false,
            "ticketing/updateTitle:done"
          );
        }
      },

      updateDescription: async (description) => {
        const t = get().ticket;
        if (!t) return;
        const opKey = key("description", t.id);
        if (get().pendingByKey[opKey]) return;

        const prevTicket = { ...t };

        set(
          (s) => ({
            pendingByKey: { ...s.pendingByKey, [opKey]: true },
            ticket: s.ticket ? ({ ...s.ticket, description, version: s.ticket.version + 1 } as any) : s.ticket
          }),
          false,
          "ticketing/updateDescription:optimistic"
        );

        try {
          const res = await API.Ticketing.updateTicketDescription(Member.orgId, t.id, { expectedVersion: prevTicket.version, description } as any);
          set({ ticket: res.ticket as any }, false, "ticketing/updateDescription:commit");
        } catch (e) {
          set(
            {
              ticket: prevTicket as any
            },
            false,
            "ticketing/updateDescription:rollback"
          );
          if (isVersionConflict(e)) {
            NotificationsModel.createToastNotification({ message: "Ticket was modified by someone else. Refreshing..." });
            void get().refreshSelected({ silent: true });
            return;
          }
          throw e;
        } finally {
          set(
            (s) => {
              const next = { ...s.pendingByKey };
              delete next[opKey];
              return { pendingByKey: next };
            },
            false,
            "ticketing/updateDescription:done"
          );
        }
      },

      updateDueDate: async (dueDate) => {
        const t = get().ticket;
        if (!t) return;
        const opKey = key("dueDate", t.id);
        if (get().pendingByKey[opKey]) return;

        const prevTicket = { ...t };

        set(
          (s) => ({
            pendingByKey: { ...s.pendingByKey, [opKey]: true },
            ticket: s.ticket ? ({ ...s.ticket, dueDate, version: s.ticket.version + 1 } as any) : s.ticket
          }),
          false,
          "ticketing/updateDueDate:optimistic"
        );

        try {
          const res = await ticketUpdate(t.id, API.TicketingAccess.updateDueDate, API.Ticketing.updateTicketDueDate, {
            expectedVersion: prevTicket.version,
            dueDate
          } as any);
          set({ ticket: res.ticket as any }, false, "ticketing/updateDueDate:commit");
        } catch (e) {
          set(
            {
              ticket: prevTicket as any
            },
            false,
            "ticketing/updateDueDate:rollback"
          );
          if (isVersionConflict(e)) {
            NotificationsModel.createToastNotification({ message: "Ticket was modified by someone else. Refreshing..." });
            void get().refreshSelected({ silent: true });
            return;
          }
          throw e;
        } finally {
          set(
            (s) => {
              const next = { ...s.pendingByKey };
              delete next[opKey];
              return { pendingByKey: next };
            },
            false,
            "ticketing/updateDueDate:done"
          );
        }
      },

      updateLocation: async (locationId) => {
        const t = get().ticket;
        if (!t) return;
        const opKey = key("location", t.id);
        if (get().pendingByKey[opKey]) return;

        const prevTicket = { ...t };

        set(
          (s) => ({
            pendingByKey: { ...s.pendingByKey, [opKey]: true },
            ticket: s.ticket ? ({ ...s.ticket, locationId, version: s.ticket.version + 1 } as any) : s.ticket,
            inboxRows: s.inboxRows.map((r) => (r.id === t.id ? { ...r, locationId } : r))
          }),
          false,
          "ticketing/updateLocation:optimistic"
        );

        try {
          const res = await API.Ticketing.updateTicketLocation(Member.orgId, t.id, { expectedVersion: prevTicket.version, locationId } as any);
          set({ ticket: res.ticket as any }, false, "ticketing/updateLocation:commit");
        } catch (e) {
          set(
            (s) => ({
              ticket: prevTicket as any,
              inboxRows: s.inboxRows.map((r) => (r.id === prevTicket.id ? { ...r, locationId: prevTicket.locationId } : r))
            }),
            false,
            "ticketing/updateLocation:rollback"
          );
          if (isVersionConflict(e)) {
            NotificationsModel.createToastNotification({ message: "Ticket was modified by someone else. Refreshing..." });
            void get().refreshSelected({ silent: true });
            return;
          }
          throw e;
        } finally {
          set(
            (s) => {
              const next = { ...s.pendingByKey };
              delete next[opKey];
              return { pendingByKey: next };
            },
            false,
            "ticketing/updateLocation:done"
          );
        }
      },

      updateUser: async (userId, userName) => {
        const t = get().ticket;
        if (!t) return;
        const opKey = key("user", t.id);
        if (get().pendingByKey[opKey]) return;

        const prevTicket = { ...t };
        const prevResolved = get().resolved;

        set(
          (s) => ({
            pendingByKey: { ...s.pendingByKey, [opKey]: true },
            ticket: s.ticket ? ({ ...s.ticket, userId, version: s.ticket.version + 1 } as any) : s.ticket,
            resolved: {
              ...s.resolved,
              user: userId && userName ? { id: userId, name: userName } : undefined
            },
            inboxRows: s.inboxRows.map((r) => (r.id === t.id ? { ...r, userId } : r))
          }),
          false,
          "ticketing/updateUser:optimistic"
        );

        try {
          const res = await API.Ticketing.updateTicketUser(Member.orgId, t.id, { expectedVersion: prevTicket.version, userId } as any);
          set({ ticket: res.ticket as any }, false, "ticketing/updateUser:commit");
        } catch (e) {
          set(
            (s) => ({
              ticket: prevTicket as any,
              resolved: prevResolved,
              inboxRows: s.inboxRows.map((r) => (r.id === prevTicket.id ? { ...r, userId: prevTicket.userId } : r))
            }),
            false,
            "ticketing/updateUser:rollback"
          );
          if (isVersionConflict(e)) {
            NotificationsModel.createToastNotification({ message: "Ticket was modified by someone else. Refreshing..." });
            void get().refreshSelected({ silent: true });
            return;
          }
          throw e;
        } finally {
          set(
            (s) => {
              const next = { ...s.pendingByKey };
              delete next[opKey];
              return { pendingByKey: next };
            },
            false,
            "ticketing/updateUser:done"
          );
        }
      },

      addNote: async (body) => {
        const t = get().ticket;
        if (!t) return;

        const tempId = -Math.floor(Math.random() * 1_000_000);
        const now = new Date().toISOString();

        set(
          (s) => ({
            notes: [
              ...s.notes,
              {
                id: tempId,
                orgId: t.orgId,
                ticketId: t.id,
                createdAt: now as any,
                updatedAt: now as any,
                actorType: "member" as any,
                actorId: null,
                actorDisplayName: "You",
                body,
                edited: false
              } as any
            ]
          }),
          false,
          "ticketing/addNote:optimistic"
        );

        try {
          const res = accessOverride
            ? await API.TicketingAccess.addNote(accessOverride.token, { body })
            : await API.Ticketing.addNote(Member.orgId, t.id, { body });
          set((s) => ({ notes: s.notes.map((n) => (n.id === tempId ? (res.note as any) : n)) }), false, "ticketing/addNote:commit");
          await get().refreshSelected({ silent: true });
          if (!accessOverride) await get().refreshInbox();
        } catch (e) {
          set((s) => ({ notes: s.notes.filter((n) => n.id !== tempId) }), false, "ticketing/addNote:rollback");
          throw e;
        }
      },

      editNote: async (noteId, body) => {
        const prev = get().notes.find((n) => n.id === noteId);
        if (!prev) return;

        set((s) => ({ notes: s.notes.map((n) => (n.id === noteId ? { ...n, body, edited: true } : n)) }), false, "ticketing/editNote:optimistic");

        try {
          const res = accessOverride
            ? await API.TicketingAccess.editNote(accessOverride.token, noteId, { body })
            : await API.Ticketing.editNote(Member.orgId, noteId, { body });
          set((s) => ({ notes: s.notes.map((n) => (n.id === noteId ? (res.note as any) : n)) }), false, "ticketing/editNote:commit");
          await get().refreshSelected({ silent: true });
        } catch (e) {
          set((s) => ({ notes: s.notes.map((n) => (n.id === noteId ? (prev as any) : n)) }), false, "ticketing/editNote:rollback");
          throw e;
        }
      },

      deleteNote: async (noteId) => {
        const prev = get().notes.find((n) => n.id === noteId);
        if (!prev) return;

        set((s) => ({ notes: s.notes.filter((n) => n.id !== noteId) }), false, "ticketing/deleteNote:optimistic");
        try {
          if (accessOverride) await API.TicketingAccess.deleteNote(accessOverride.token, noteId);
          else await API.Ticketing.deleteNote(Member.orgId, noteId);
          await get().refreshSelected({ silent: true });
          if (!accessOverride) await get().refreshInbox();
        } catch (e) {
          set((s) => ({ notes: [...s.notes, prev] as any }), false, "ticketing/deleteNote:rollback");
          throw e;
        }
      },

      addTask: async (shortDescription) => {
        const t = get().ticket;
        if (!t) return;

        const tempId = -Math.floor(Math.random() * 1_000_000);
        const now = new Date().toISOString();

        set(
          (s) => ({
            tasks: [
              ...s.tasks,
              {
                id: tempId,
                orgId: t.orgId,
                ticketId: t.id,
                createdAt: now as any,
                updatedAt: now as any,
                deletedDate: null,
                shortDescription,
                state: TaskState.UNCOMPLETED as any,
                actionType: null,
                actionConfig: null
              } as any
            ]
          }),
          false,
          "ticketing/addTask:optimistic"
        );

        try {
          const res = await API.Ticketing.addTask(Member.orgId, t.id, { shortDescription });
          set((s) => ({ tasks: s.tasks.map((x) => (x.id === tempId ? (res.task as any) : x)) }), false, "ticketing/addTask:commit");
          await get().refreshSelected({ silent: true });
          await get().refreshInbox();
        } catch (e) {
          set((s) => ({ tasks: s.tasks.filter((x) => x.id !== tempId) }), false, "ticketing/addTask:rollback");
          throw e;
        }
      },

      setTaskState: async (taskId, state) => {
        const prev = get().tasks.find((t) => t.id === taskId);
        if (!prev) return;

        set((s) => ({ tasks: s.tasks.map((t) => (t.id === taskId ? ({ ...t, state } as any) : t)) }), false, "ticketing/setTaskState:optimistic");

        try {
          if (accessOverride) await API.TicketingAccess.setTaskState(accessOverride.token, taskId, { state });
          else await API.Ticketing.setTaskState(Member.orgId, taskId, { state });
          await get().refreshSelected({ silent: true });
          if (!accessOverride) await get().refreshInbox();
        } catch (e) {
          set((s) => ({ tasks: s.tasks.map((t) => (t.id === taskId ? (prev as any) : t)) }), false, "ticketing/setTaskState:rollback");
          throw e;
        }
      },

      deleteTask: async (taskId) => {
        const prev = get().tasks.find((t) => t.id === taskId);
        if (!prev) return;

        set((s) => ({ tasks: s.tasks.filter((t) => t.id !== taskId) }), false, "ticketing/deleteTask:optimistic");
        try {
          await API.Ticketing.deleteTask(Member.orgId, taskId);
          await get().refreshSelected({ silent: true });
          await get().refreshInbox();
        } catch (e) {
          set((s) => ({ tasks: [...s.tasks, prev] as any }), false, "ticketing/deleteTask:rollback");
          throw e;
        }
      },

      updateTaskDescription: async (taskId, shortDescription) => {
        const prev = get().tasks.find((t) => t.id === taskId);
        if (!prev) return;

        set(
          (s) => ({ tasks: s.tasks.map((t) => (t.id === taskId ? { ...t, shortDescription } : t)) }),
          false,
          "ticketing/updateTaskDescription:optimistic"
        );

        try {
          const res = await API.Ticketing.updateTaskDescription(Member.orgId, taskId, { shortDescription });
          set((s) => ({ tasks: s.tasks.map((t) => (t.id === taskId ? (res.task as any) : t)) }), false, "ticketing/updateTaskDescription:commit");
          await get().refreshSelected({ silent: true });
        } catch (e) {
          set((s) => ({ tasks: s.tasks.map((t) => (t.id === taskId ? (prev as any) : t)) }), false, "ticketing/updateTaskDescription:rollback");
          throw e;
        }
      },

      updateTaskAction: async (taskId, actionType, actionConfig) => {
        const prev = get().tasks.find((t) => t.id === taskId);
        if (!prev) return;

        set(
          (s) => ({ tasks: s.tasks.map((t) => (t.id === taskId ? { ...t, actionType, actionConfig } : t)) }),
          false,
          "ticketing/updateTaskAction:optimistic"
        );

        try {
          const res = await API.Ticketing.updateTaskAction(Member.orgId, taskId, { actionType, actionConfig });
          set((s) => ({ tasks: s.tasks.map((t) => (t.id === taskId ? (res.task as any) : t)) }), false, "ticketing/updateTaskAction:commit");
          await get().refreshSelected({ silent: true });
        } catch (e) {
          set((s) => ({ tasks: s.tasks.map((t) => (t.id === taskId ? (prev as any) : t)) }), false, "ticketing/updateTaskAction:rollback");
          throw e;
        }
      },

      reorderTasks: async (taskIds) => {
        const t = get().ticket;
        if (!t) return;

        const prevTasks = [...get().tasks];
        // Reorder locally based on taskIds order
        const reordered = taskIds.map((id) => prevTasks.find((task) => task.id === id)).filter(Boolean) as typeof prevTasks;

        set({ tasks: reordered }, false, "ticketing/reorderTasks:optimistic");

        try {
          await API.Ticketing.reorderTasks(Member.orgId, t.id, { taskIds });
          await get().refreshSelected({ silent: true });
        } catch (e) {
          set({ tasks: prevTasks }, false, "ticketing/reorderTasks:rollback");
          throw e;
        }
      },

      onTicketUpdated: (incomingTicket) => {
        // Surface a "New ticket assigned to {group} group" toast when a new
        // open-to-group ticket lands in one of my groups (deduped internally).
        get().maybeAlertNewGroupTicket(incomingTicket);

        const currentState = get();

        // No user-facing toast here: note and assignment notifications come from
        // NotificationDispatchService, gated by each member's notification preferences.
        // This handler only syncs live ticket state into the inbox/detail views.

        // Check if the incoming ticket matches the current inbox filters
        const q = currentState.inboxQuery;
        const matchesFilters = (): boolean => {
          // Check status filter
          if (q.statuses?.length && !q.statuses.includes(incomingTicket.status)) {
            return false;
          }
          // Check single status filter
          if (q.status && q.status !== incomingTicket.status) {
            return false;
          }
          // Check priority filter
          if (q.priority && q.priority !== incomingTicket.priority) {
            return false;
          }
          // Check assignee filter
          if (q.assigneeMemberId !== undefined) {
            if (q.assigneeMemberId === 0) {
              // Unassigned filter: ticket should have no assignees
              if (incomingTicket.assigneeMemberIds?.length) {
                return false;
              }
            } else if (!incomingTicket.assigneeMemberIds?.includes(q.assigneeMemberId)) {
              return false;
            }
          }
          // Check location filter
          if (q.locationIds?.length && !q.locationIds.includes(incomingTicket.locationId!)) {
            return false;
          }
          if (q.locationId !== undefined && q.locationId !== incomingTicket.locationId) {
            return false;
          }
          // Check createdByMemberId filter
          if (q.createdByMemberId !== undefined && q.createdByMemberId !== incomingTicket.createdByMemberId) {
            return false;
          }
          return true;
        };

        const ticketMatchesFilters = matchesFilters();

        // Update the inbox row if it exists, or add it if new (only if it matches filters)
        set(
          (s) => {
            const existingIndex = s.inboxRows.findIndex((r) => r.id === incomingTicket.id);
            let newRows: TicketPreview[];

            if (existingIndex >= 0) {
              if (ticketMatchesFilters) {
                // Update existing row
                newRows = s.inboxRows.map((r) =>
                  r.id === incomingTicket.id
                    ? { ...r, ...incomingTicket, hasUnreadActivity: computeHasUnread(r.id, incomingTicket.lastActivityAt) }
                    : r
                );
              } else {
                // Remove the row since it no longer matches filters
                newRows = s.inboxRows.filter((r) => r.id !== incomingTicket.id);
              }
            } else if (ticketMatchesFilters) {
              // New ticket that matches filters - add to the beginning of the list
              newRows = [
                { ...incomingTicket, hasUnreadActivity: computeHasUnread(incomingTicket.id, incomingTicket.lastActivityAt) },
                ...s.inboxRows
              ];
            } else {
              // New ticket that doesn't match filters - don't add it
              newRows = s.inboxRows;
            }

            // Also update lastActivityByTicketId if we have activity info
            const lastMap = { ...s.lastActivityByTicketId };
            if (incomingTicket.lastActivityAt && incomingTicket.lastActivityType) {
              lastMap[incomingTicket.id] = {
                at: incomingTicket.lastActivityAt,
                type: incomingTicket.lastActivityType
              };
            }

            return { inboxRows: newRows, lastActivityByTicketId: lastMap };
          },
          false,
          "ticketing/onTicketUpdated"
        );

        // If this is the currently selected ticket, refresh detail view
        if (get().selectedTicketId === incomingTicket.id) {
          void get().refreshSelected({ silent: true });
        }
      }
    }),
    { name: "ticketingStore" }
  )
);
